import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { ArrowClockwise, CheckCircleFill, XCircleFill } from 'react-bootstrap-icons';
import { reasonOf } from '@/components/tools/clientSelectionUtils';
import { CopyButton } from '@/components/ui/CopyButton';
import { ToolPage } from '@/components/ui/ToolPage';
import { useAuth } from '@/contexts/AuthContext';
import { clientService } from '@/services/clientService';
import { fixedIpService, type FixedIpRow, type FixedIpState } from '@/services/fixedIpService';
import { formatNzDateTime, timeAgo } from '@/utils/relativeTime';
import './FixedIpFleet.css';

const STATE_BADGE: Record<FixedIpState, { bg: string; label: string }> = {
  active: { bg: 'success', label: 'Working' },
  resuming: { bg: 'info', label: 'Starting' },
  starting: { bg: 'info', label: 'Starting on a new address' },
  paused: { bg: 'warning', label: 'Paused' },
  suspended: { bg: 'danger', label: 'Paused: out of credits' },
  off: { bg: 'secondary', label: 'Turned off' },
};

/** A past time as "3 min ago", with the exact New Zealand time on hover. */
const ago = (iso?: string) =>
  iso ? <span title={`${formatNzDateTime(iso)} New Zealand time`}>{timeAgo(iso)}</span> : null;

/**
 * Tools > Fixed outgoing IP: every client with a fixed IP, its state, its
 * last health check and this month's charge, with Pause and Resume.
 *
 * A pause here is the same pause an admin makes in the client's Settings:
 * Numa's calls to that client's servers fail until it resumes, and the
 * client's admins are told. There is no failover region.
 */
export default function FixedIpFleet() {
  const { user } = useAuth();
  const [rows, setRows] = useState<FixedIpRow[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});

  const [pausing, setPausing] = useState<FixedIpRow | null>(null);
  const [hours, setHours] = useState('');
  const [reason, setReason] = useState('');
  const [resuming, setResuming] = useState<FixedIpRow | null>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    setRefreshing(true);
    try {
      setRows(await fixedIpService.fleet(await clientService.getAllClients()));
    } catch (error) {
      setLoadError(reasonOf(error));
      setRows([]);
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const replace = (name: string, next: Partial<FixedIpRow>) =>
    setRows((prev) => (prev ?? []).map((r) => (r.client.name === name ? { ...r, ...next } : r)));

  const by = user?.email || user?.username || 'Arcanum';

  const doPause = async () => {
    if (!pausing) return;
    const name = pausing.client.name;
    setBusy(name);
    setRowError((e) => ({ ...e, [name]: '' }));
    try {
      const status = await fixedIpService.pause(pausing.client, by, hours ? Number(hours) : null, reason);
      replace(name, { status, error: undefined });
      setPausing(null);
    } catch (error) {
      setRowError((e) => ({ ...e, [name]: error instanceof Error ? error.message : String(error) }));
      setPausing(null);
    } finally {
      setBusy(null);
    }
  };

  const doResume = async () => {
    if (!resuming) return;
    const name = resuming.client.name;
    setBusy(name);
    setRowError((e) => ({ ...e, [name]: '' }));
    try {
      replace(name, { status: await fixedIpService.resume(resuming.client, by), error: undefined });
    } catch (error) {
      setRowError((e) => ({ ...e, [name]: error instanceof Error ? error.message : String(error) }));
    } finally {
      setResuming(null);
      setBusy(null);
    }
  };

  return (
    <ToolPage
      title="Fixed outgoing IP"
      subtitle="Check that each client's calls leave from its fixed IP, and pause or resume one."
      actions={
        <Button variant="outline-secondary" onClick={() => void load()} disabled={rows === null || refreshing}>
          {refreshing && rows !== null ? (
            <Spinner size="sm" className="me-1" />
          ) : (
            <ArrowClockwise className="me-1" aria-hidden="true" />
          )}
          Refresh
        </Button>
      }
    >
      <p className="fixed-ip-note">
        While a fixed IP is paused, Numa&apos;s calls to that client&apos;s servers fail. There is no second region to
        fall back to.
      </p>

      {loadError && <Alert variant="danger">The list did not load. {loadError} Press Refresh to try again.</Alert>}

      <Card>
        <Table responsive hover className="mb-0 fixed-ip-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Fixed IP</th>
              <th>State</th>
              <th>Last check</th>
              <th className="text-end">This month</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr>
                <td colSpan={6} className="fixed-ip-empty">
                  <Spinner animation="border" size="sm" className="me-2" />
                  Asking each client
                </td>
              </tr>
            )}
            {rows !== null && rows.length === 0 && loadError && (
              <tr>
                <td colSpan={6} className="fixed-ip-empty">
                  Nothing to show until the list loads.
                </td>
              </tr>
            )}
            {rows !== null && rows.length === 0 && !loadError && (
              <tr>
                <td colSpan={6} className="fixed-ip-empty">
                  No client has a fixed IP. Turn on &quot;Fixed outgoing IP&quot; in a client&apos;s config to give it
                  one.
                </td>
              </tr>
            )}
            {(rows ?? []).map((row) => {
              const s = row.status;
              const state = s?.state ?? 'active';
              const badge = STATE_BADGE[state] ?? STATE_BADGE.active;
              return (
                <tr key={row.client.name}>
                  <td>
                    <div className="fixed-ip-client">{row.client.name}</div>
                    {s?.region && <div className="fixed-ip-sub mono">{s.region}</div>}
                  </td>
                  <td>
                    {s?.ip && (
                      <>
                        <span className="mono">{s.ip}</span>
                        <CopyButton value={s.ip} label="Copy the IP" />
                      </>
                    )}
                  </td>
                  <td>
                    {row.error ? (
                      <Badge bg="secondary">Not known</Badge>
                    ) : s && !s.enabled ? (
                      <Badge bg="secondary">Not built yet</Badge>
                    ) : (
                      <>
                        <Badge bg={badge.bg}>{badge.label}</Badge>
                        {(state === 'paused' || state === 'suspended') && (
                          <div className="fixed-ip-sub">
                            {(s?.state_by || s?.state_at) && (
                              <div>
                                {s?.state_by && <>By {s.state_by} </>}
                                {ago(s?.state_at)}
                              </div>
                            )}
                            {s?.resume_at && <div>Resumes {formatNzDateTime(s.resume_at)} New Zealand time</div>}
                            {s?.state_reason && <div>Reason: {s.state_reason}</div>}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                  <td>
                    {row.error && <span className="fixed-ip-bad">This client did not answer: {row.error}</span>}
                    {!row.error && s?.health?.checked_at && (
                      <>
                        {s.health.ok ? (
                          <span className="fixed-ip-good">
                            <CheckCircleFill aria-hidden="true" /> Passed {ago(s.health.checked_at)},{' '}
                            {s.health.latency_ms ?? 0} ms
                          </span>
                        ) : (
                          <span className="fixed-ip-bad">
                            <XCircleFill aria-hidden="true" /> Failing
                            {s.health.since && <> since {formatNzDateTime(s.health.since)} New Zealand time</>}
                          </span>
                        )}
                        {!s.health.ok && s.health.detail && <div className="fixed-ip-sub">{s.health.detail}</div>}
                      </>
                    )}
                    {!row.error && s?.enabled && !s.health?.checked_at && (
                      <span className="text-muted">No check yet</span>
                    )}
                    {rowError[row.client.name] && (
                      <div className="fixed-ip-bad">The change did not go through: {rowError[row.client.name]}</div>
                    )}
                  </td>
                  <td className="text-end numeric">
                    {s?.month && (
                      <>
                        <div>{s.month.credits.toLocaleString()} credits</div>
                        <div className="fixed-ip-sub">US${s.month.cost_usd.toFixed(2)} AWS cost</div>
                      </>
                    )}
                  </td>
                  <td className="fixed-ip-actions">
                    {s?.enabled && state === 'active' && (
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={busy !== null}
                        onClick={() => {
                          setHours('');
                          setReason('');
                          setPausing(row);
                        }}
                      >
                        Pause
                      </Button>
                    )}
                    {s?.enabled && state === 'paused' && (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        disabled={busy !== null}
                        onClick={() => setResuming(row)}
                      >
                        {busy === row.client.name && <Spinner size="sm" className="me-1" />}
                        Resume
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card>

      <Modal show={pausing !== null} onHide={() => setPausing(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Pause the fixed IP for {pausing?.client.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="warning">
            While it is paused, Numa&apos;s calls to this client&apos;s servers fail. The client&apos;s admins are told.
            The IP, the firewall rule and every connector&apos;s setting are kept.
          </Alert>
          <Form.Group className="mb-3" controlId="fixed-ip-fleet-hours">
            <Form.Label>Pause for</Form.Label>
            <Form.Select value={hours} onChange={(e) => setHours(e.target.value)}>
              <option value="">Until somebody resumes it</option>
              <option value="1">1 hour</option>
              <option value="4">4 hours</option>
              <option value="24">1 day</option>
              <option value="168">1 week</option>
            </Form.Select>
          </Form.Group>
          <Form.Group controlId="fixed-ip-fleet-reason">
            <Form.Label>Reason</Form.Label>
            <Form.Control value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
            <Form.Text>The client&apos;s admins see this.</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPausing(null)} disabled={busy !== null}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => void doPause()} disabled={busy !== null}>
            {busy && <Spinner size="sm" className="me-1" />}
            Pause the fixed IP
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={resuming !== null} onHide={() => setResuming(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Resume the fixed IP for {resuming?.client.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          The fixed IP starts again. Calls to this client&apos;s servers wait until the next check sees them leave from
          the IP, which takes a few minutes. The client&apos;s admins are told.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setResuming(null)} disabled={busy !== null}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void doResume()} disabled={busy !== null}>
            {busy && <Spinner size="sm" className="me-1" />}
            Resume the fixed IP
          </Button>
        </Modal.Footer>
      </Modal>
    </ToolPage>
  );
}
